/**
 * PreChemoBilanEncart — Sprint 6
 *
 * Encart affichant les bilans pré-chimio à traiter.
 * Utilisé par :
 *  - IDE Dashboard (mode="ide") : upload labo + bilans PENDING_IDE avec tri RAS / ANOMALY
 *  - MEDECIN Dashboard (mode="doctor") : bilans PENDING_DOCTOR avec actions VALIDATE / REFUSE
 *
 * Props :
 *   - mode : "ide" | "doctor"
 */
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { ClipboardCheck, CheckCircle2, AlertOctagon, X, FileText, User, FlaskConical, Upload } from "lucide-react";
import api from "@/api";
import BiologyQualificationModal from "@/components/clinical/BiologyQualificationModal";

const API_BY_MODE = {
  ide: "/pre-chemo-bilans/pending-ide",
  doctor: "/pre-chemo-bilans/pending-doctor",
};

function SourceBadge({ source, labId, uploadedByRole }) {
  const normalizedSource = source || uploadedByRole;
  if (normalizedSource === "PATIENT") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-200" data-testid="prechemo-source-patient">
        <User className="w-3 h-3" />
        Uploadé par le patient
      </span>
    );
  }
  if (normalizedSource === "IDE_INTAKE" || normalizedSource === "IDE") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid="prechemo-source-ide">
        <ClipboardCheck className="w-3 h-3" />
        IDE
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 border border-sky-200" data-testid="prechemo-source-lab">
      <FlaskConical className="w-3 h-3" />
      Labo
    </span>
  );
}

// Catégories pertinentes pour une injection alternative (post refus chimio).
// Affichées dans le dropdown médicament.
const INJECTION_RELEVANT_CATEGORIES = [
  "GCSF",
  "EPO",
  "BISPHOSPHONATE",
  "DECAPEPTYL",
  "CORTICOID",
  "ANTIEMETIC",
  "OTHER",
];

const CATEGORY_LABELS_FR = {
  GCSF: "G-CSF (facteurs de croissance)",
  EPO: "EPO (érythropoïétine)",
  BISPHOSPHONATE: "Bisphosphonates / Anti-RANKL",
  DECAPEPTYL: "Analogues GnRH (Decapeptyl)",
  CORTICOID: "Corticoïdes",
  ANTIEMETIC: "Anti-émétiques",
  OTHER: "Autres",
  HYDRATATION: "Hydratation",
};

const normalizeSearch = (value = "") =>
  value
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const getProtocolSearchText = (protocol) => {
  const components = (protocol.components || [])
    .map((c) => `${c.dci || ""} ${c.doseFormula || ""} ${c.route || ""} ${c.days || ""}`)
    .join(" ");
  return normalizeSearch(`${protocol.code || ""} ${protocol.label || ""} ${protocol.category || ""} ${components}`);
};

function RefuseModal({ open, bilanId, onClose, onRefused }) {
  const [reason, setReason] = useState("");
  const [cat, setCat] = useState("");
  const [withInjection, setWithInjection] = useState(false);
  const [drugCode, setDrugCode] = useState(""); // "__custom" pour saisie libre
  const [injDrug, setInjDrug] = useState("");
  const [injDose, setInjDose] = useState("");
  const [injRoute, setInjRoute] = useState("");
  const [injDate, setInjDate] = useState("");
  const [injNotes, setInjNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [drugCatalog, setDrugCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogOpen, setCatalogOpen] = useState(false);

  useEffect(() => {
  }, [open, bilanId]);

  // Charge le catalogue à l'ouverture (1 seule fois)
  useEffect(() => {
    if (!open || drugCatalog.length > 0) return;
    let cancelled = false;
    setLoadingCatalog(true);
    api.get("/reference-protocols", { params: { kind: "COMPLEMENTARY", activeOnly: true, limit: 500 } })
      .then(({ data }) => {
        if (cancelled) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        // Filtre catégories injection-pertinentes uniquement
        setDrugCatalog(
          items
            .filter(i => INJECTION_RELEVANT_CATEGORIES.includes(i.category))
            .sort((a, b) => (a.label || "").localeCompare(b.label || ""))
        );
      })
      .catch(() => { if (!cancelled) setDrugCatalog([]); })
      .finally(() => { if (!cancelled) setLoadingCatalog(false); });
    return () => { cancelled = true; };
  }, [open, drugCatalog.length]);

  // Auto-remplissage dose / route quand on choisit un médicament du catalogue
  const handleDrugSelect = (code) => {
    setDrugCode(code);
    if (code === "" || code === "__custom") {
      // ne touche pas aux champs : laisser vide / saisie libre
      if (code === "") {
        setInjDrug("");
        setInjDose("");
        setInjRoute("");
      }
      return;
    }
    const proto = drugCatalog.find(p => p.code === code);
    if (!proto) return;
    const firstComp = (proto.components || [])[0] || {};
    setInjDrug(proto.label || firstComp.dci || "");
    setInjDose(firstComp.doseFormula || "");
    setInjRoute(firstComp.route || "");
    setCatalogSearch(proto.label || firstComp.dci || "");
    setCatalogOpen(false);
  };

  const filteredCatalog = useMemo(() => {
    const search = normalizeSearch(catalogSearch);
    const source = search
      ? drugCatalog.filter((p) => getProtocolSearchText(p).includes(search))
      : drugCatalog;
    return source.slice(0, 12);
  }, [catalogSearch, drugCatalog]);

  const handleCatalogSearchChange = (value) => {
    setCatalogSearch(value);
    setCatalogOpen(true);
    if (!value.trim()) {
      setDrugCode("");
      return;
    }
    if (drugCode && drugCode !== "__custom") {
      setDrugCode("");
    }
  };

  if (!open) return null;

  const submit = async () => {
    if (reason.trim().length < 10) {
      toast.error("La raison doit contenir au moins 10 caractères");
      return;
    }
    if (cat.trim().length < 10) {
      toast.error("La conduite à tenir doit contenir au moins 10 caractères");
      return;
    }
    if (withInjection) {
      if (!injDrug.trim() || !injDose.trim() || !injRoute.trim()) {
        toast.error("Pour l'injection : médicament, dose et voie sont obligatoires");
        return;
      }
    }
    setSubmitting(true);
    try {
      const body = {
        outcome: withInjection ? "REFUSE_WITH_INJECTION" : "REFUSE",
        refusalReason: reason.trim(),
        cat: cat.trim(),
      };
      if (withInjection) {
        body.injectionPrescription = {
          drug: injDrug.trim(),
          dose: injDose.trim(),
          route: injRoute.trim(),
          scheduledDate: injDate || null,
          notes: injNotes.trim() || null,
          referenceCode: drugCode && drugCode !== "__custom" ? drugCode : null,
        };
      }
      const { data } = await api.post(`/pre-chemo-bilans/${bilanId}/doctor-action`, body);
      toast.success(data?.injectionAppointmentId
        ? "Bilan refusé — ordre transmis à l'IDE et à la secrétaire, séance injection HDJ créée"
        : "Bilan refusé — ordre transmis à l'IDE et à la secrétaire");
      onRefused?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 overflow-y-auto" data-testid="refuse-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md my-auto">
        <div className="p-5 border-b border-slate-200 flex items-start justify-between">
          <div>
            <h3 className="font-bold text-slate-900">Refuser le bilan</h3>
            <p className="text-xs text-slate-500 mt-0.5">Votre décision et votre CAT partent dans la relève IDE et le carnet secrétariat</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="refuse-modal-close-button">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Raison du refus <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={2}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Ex : neutropénie sévère, plaquettes < 75"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="refuse-reason-input"
            />
            <p className="text-xs text-slate-400 mt-0.5">Min 10 caractères ({reason.length})</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Conduite à tenir (CAT) <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              value={cat}
              onChange={e => setCat(e.target.value)}
              placeholder="Ex : reporter la chimio de 7 jours, G-CSF 30 MUI x5 jours, contrôle NFS à J5"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="refuse-cat-input"
            />
            <p className="text-xs text-slate-400 mt-0.5">Min 10 caractères — sera copié tel quel pour la secrétaire ({cat.length})</p>
          </div>

          {/* Injection alternative — toggle */}
          <div className="border-t border-slate-200 pt-4">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="refuse-with-injection-toggle">
              <input
                type="checkbox"
                checked={withInjection}
                onChange={e => setWithInjection(e.target.checked)}
                className="mt-1 w-4 h-4 accent-violet-600"
                data-testid="refuse-with-injection-checkbox"
              />
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  Demander une injection alternative en HDJ (ordre, pas d'ordonnance)
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Le patient passera quand même en HDJ pour cette injection (ex : G-CSF, EPO, Decapeptyl).
                  Un RDV de type « injection_hdj » sera créé automatiquement.
                </p>
              </div>
            </label>

            {withInjection && (
              <div className="mt-3 p-3 rounded-lg bg-violet-50 border border-violet-100 space-y-3" data-testid="refuse-injection-block">
                {/* Catalogue médicaments — recherche branchée au catalogue referenceProtocols */}
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Catalogue médicaments
                    {loadingCatalog && <span className="text-slate-400 italic ml-1">(chargement…)</span>}
                  </label>
                  <div className="relative" data-testid="injection-catalog-combobox">
                    <input
                      value={catalogSearch}
                      onChange={e => handleCatalogSearchChange(e.target.value)}
                      onFocus={() => setCatalogOpen(true)}
                      placeholder="Rechercher : Decap, G-CSF, EPO, Xgeva…"
                      className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-200"
                      data-testid="injection-catalog-search-input"
                      autoComplete="off"
                    />
                    {catalogOpen && (
                      <div className="absolute z-[120] mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-xl" data-testid="injection-catalog-results">
                        {loadingCatalog && (
                          <div className="px-3 py-2 text-xs text-slate-500" data-testid="injection-catalog-loading">
                            Chargement du catalogue…
                          </div>
                        )}
                        {!loadingCatalog && filteredCatalog.map((p) => {
                          const firstComp = (p.components || [])[0] || {};
                          return (
                            <button
                              key={p.code}
                              type="button"
                              onClick={() => handleDrugSelect(p.code)}
                              className="w-full text-left px-3 py-2 hover:bg-violet-50 border-b border-slate-50 last:border-b-0 transition-colors"
                              data-testid={`injection-catalog-option-${p.code}`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-slate-800 truncate">{p.label}</p>
                                  <p className="text-[11px] text-slate-500 truncate">
                                    {(CATEGORY_LABELS_FR[p.category] || p.category || "Catalogue")}
                                    {firstComp.doseFormula ? ` · ${firstComp.doseFormula}` : ""}
                                    {firstComp.route ? ` · ${firstComp.route}` : ""}
                                  </p>
                                </div>
                                <span className="text-[10px] font-semibold text-violet-700 bg-violet-50 rounded-full px-2 py-0.5 flex-shrink-0">
                                  {p.code}
                                </span>
                              </div>
                            </button>
                          );
                        })}
                        {!loadingCatalog && filteredCatalog.length === 0 && (
                          <div className="px-3 py-2 text-xs text-slate-500" data-testid="injection-catalog-no-results">
                            Aucun médicament trouvé dans le catalogue pour “{catalogSearch}”. Utilisez la saisie manuelle ci-dessous.
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setDrugCode("__custom");
                            setCatalogOpen(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                          data-testid="injection-catalog-custom-option"
                        >
                          ✏️ Autre — saisir manuellement
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Recherchez dans le catalogue pour pré-remplir dose et voie. Les champs restent éditables.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Médicament <span className="text-red-500">*</span>
                    </label>
                    <input
                      value={injDrug}
                      onChange={e => setInjDrug(e.target.value)}
                      placeholder="Ex : G-CSF, Decapeptyl"
                      className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm"
                      data-testid="injection-drug-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Dose <span className="text-red-500">*</span>
                    </label>
                    <input
                      value={injDose}
                      onChange={e => setInjDose(e.target.value)}
                      placeholder="Ex : 30 MUI"
                      className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm"
                      data-testid="injection-dose-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Voie <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={injRoute}
                      onChange={e => setInjRoute(e.target.value)}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm bg-white"
                      data-testid="injection-route-select"
                    >
                      <option value="">— Choisir —</option>
                      <option value="SC">Sous-cutanée (SC)</option>
                      <option value="IM">Intramusculaire (IM)</option>
                      <option value="IV">Intraveineuse (IV)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Date HDJ</label>
                    <input
                      type="date"
                      value={injDate}
                      onChange={e => setInjDate(e.target.value)}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm"
                      data-testid="injection-date-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Notes (optionnel)</label>
                  <textarea
                    rows={2}
                    value={injNotes}
                    onChange={e => setInjNotes(e.target.value)}
                    placeholder="Précisions pour l'IDE en HDJ"
                    className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm"
                    data-testid="injection-notes-input"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex gap-2 justify-end">
              <button
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg"
                data-testid="refuse-cancel-button"
              >
                Annuler
              </button>
              <button
                onClick={submit}
                disabled={submitting}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:bg-slate-300 rounded-lg"
                data-testid="refuse-submit-btn"
              >
                {submitting ? "Envoi…" : (withInjection ? "Refuser + Injection" : "Confirmer le refus")}
              </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function PreChemoBilanEncart({ mode }) {
  const [bilans, setBilans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [refuseFor, setRefuseFor] = useState(null);
  const [showIdeUpload, setShowIdeUpload] = useState(false);
  const [uploadPatients, setUploadPatients] = useState([]);
  const [uploadPatientId, setUploadPatientId] = useState("");
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [ideAnomalyFor, setIdeAnomalyFor] = useState(null);

  const isIde = mode === "ide";
  const title = isIde ? "Console IDE — bilans pré-chimio" : "Bilans pré-chimio à valider";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(API_BY_MODE[mode]);
      setBilans(Array.isArray(data) ? data : []);
    } catch {
      setBilans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [mode]);

  useEffect(() => {
    if (!isIde) return;
    let cancelled = false;
    setLoadingPatients(true);
    api.get("/patients", { params: { limit: 500 } })
      .then(({ data }) => {
        if (cancelled) return;
        const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
        setUploadPatients(items);
      })
      .catch(() => setUploadPatients([]))
      .finally(() => { if (!cancelled) setLoadingPatients(false); });
    return () => { cancelled = true; };
  }, [isIde]);

  const runIdeAction = async (bilanId, outcome, extra = {}) => {
    setBusyId(bilanId);
    try {
      await api.post(`/pre-chemo-bilans/${bilanId}/ide-action`, { outcome, ...extra });
      toast.success(
        outcome === "RAS"
          ? "Tri RAS transmis au médecin pour Go/No-Go"
          : "Anomalie transmise au médecin pour Go/No-Go"
      );
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBusyId(null);
    }
  };

  const runDoctorValidate = async (bilanId) => {
    setBusyId(bilanId);
    try {
      await api.post(`/pre-chemo-bilans/${bilanId}/doctor-action`, { outcome: "VALIDATE" });
      toast.success("Bilan validé — mission créée pour la secrétaire");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="dash-card border-l-4 border-violet-400" data-testid={`pre-chemo-encart-${mode}`}>
      <div className="flex items-start sm:items-center justify-between gap-3 mb-4">
        <h2 className="text-base font-semibold text-slate-800 flex items-center gap-3 leading-tight">
          <div className="p-2 bg-violet-50 rounded-xl">
            <ClipboardCheck className="w-5 h-5 text-violet-600" strokeWidth={2} />
          </div>
          {title}
          {bilans.length > 0 && (
            <span className="bg-violet-100 text-violet-700 text-xs px-2 py-0.5 rounded-full font-medium">
              {bilans.length}
            </span>
          )}
        </h2>
        {isIde && (
          <button
            type="button"
            onClick={() => setShowIdeUpload(true)}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 transition-colors min-h-[40px]"
            data-testid="ide-upload-lab-result-button"
          >
            <Upload className="w-4 h-4" />
            Ajouter résultat labo
          </button>
        )}
      </div>

      {isIde && (
        <div className="mb-4 rounded-xl border border-emerald-100 bg-emerald-50/70 p-3" data-testid="ide-lab-intake-note">
          <p className="text-xs font-semibold text-emerald-900" data-testid="ide-lab-intake-title">
            L&apos;IDE centralise les résultats reçus hors Hana comme le laboratoire.
          </p>
          <p className="text-[11px] text-emerald-800 mt-1" data-testid="ide-lab-intake-description">
            Après upload IDE, le bilan qualifié part directement au médecin. Seuls les bilans envoyés par le patient restent ici pour tri RAS/anomalie.
          </p>
        </div>
      )}

      {loading ? (
        <div className="py-6 text-center text-slate-400 text-sm">Chargement…</div>
      ) : bilans.length === 0 ? (
        <div className="text-center py-8 text-slate-400">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
          <p className="text-sm">Aucun bilan en attente</p>
        </div>
      ) : (
        <div className="space-y-3">
          {bilans.map(b => (
            <BilanCard
              key={b.id}
              bilan={b}
              mode={mode}
              busy={busyId === b.id}
              onIdeValidate={() => runIdeAction(b.id, "RAS")}
              onIdeAskDoctor={() => setIdeAnomalyFor(b)}
              onDoctorValidate={() => runDoctorValidate(b.id)}
              onDoctorRefuse={() => setRefuseFor(b.id)}
            />
          ))}
        </div>
      )}

      <RefuseModal
        open={!!refuseFor}
        bilanId={refuseFor}
        onClose={() => setRefuseFor(null)}
        onRefused={load}
      />

      {isIde && ideAnomalyFor && (
        <IdeAnomalyModal
          bilan={ideAnomalyFor}
          busy={busyId === ideAnomalyFor.id}
          onClose={() => setIdeAnomalyFor(null)}
          onSubmit={async ({ anomalies, note }) => {
            await runIdeAction(ideAnomalyFor.id, "ANOMALY", { anomalies, note });
            setIdeAnomalyFor(null);
          }}
        />
      )}

      {isIde && showIdeUpload && (
        <IdeLabUploadPicker
          patients={uploadPatients}
          selectedPatientId={uploadPatientId}
          setSelectedPatientId={setUploadPatientId}
          loadingPatients={loadingPatients}
          onClose={() => setShowIdeUpload(false)}
          onSuccess={() => {
            setShowIdeUpload(false);
            setUploadPatientId("");
            load();
          }}
        />
      )}
    </div>
  );
}

function IdeLabUploadPicker({ patients, selectedPatientId, setSelectedPatientId, loadingPatients, onClose, onSuccess }) {
  const selected = patients.find((p) => p.id === selectedPatientId);
  const selectedName = selected ? `${selected.firstName || ""} ${selected.lastName || ""}`.trim() : "";

  if (selectedPatientId) {
    return (
      <BiologyQualificationModal
        patientId={selectedPatientId}
        patientName={selectedName}
        onClose={onClose}
        onSuccess={onSuccess}
      />
    );
  }

  return createPortal(
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4" data-testid="ide-lab-upload-patient-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-5 border-b border-slate-200 flex items-start justify-between">
          <div>
            <h3 className="font-bold text-slate-900" data-testid="ide-lab-upload-patient-title">Ajouter un résultat labo</h3>
            <p className="text-xs text-slate-500 mt-0.5" data-testid="ide-lab-upload-patient-subtitle">Sélectionnez le patient concerné</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="ide-lab-upload-patient-close-button" type="button">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="ide-lab-upload-patient-select">
            Patient
          </label>
          <select
            id="ide-lab-upload-patient-select"
            value={selectedPatientId}
            onChange={(e) => setSelectedPatientId(e.target.value)}
            disabled={loadingPatients}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm"
            data-testid="ide-lab-upload-patient-select"
          >
            <option value="">{loadingPatients ? "Chargement…" : "— Choisir —"}</option>
            {patients.map((p) => (
              <option key={p.id} value={p.id}>
                {`${p.firstName || ""} ${p.lastName || ""}`.trim() || p.phone || p.id}
              </option>
            ))}
          </select>
          <p className="text-xs text-slate-500" data-testid="ide-lab-upload-patient-help">
            Après sélection, vous pourrez uploader le PDF et qualifier RAS ou anomalies.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}

function IdeAnomalyModal({ bilan, busy, onClose, onSubmit }) {
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState([]);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/lab/anomalies")
      .then(({ data }) => { if (!cancelled) setCategories(Array.isArray(data) ? data : []); })
      .catch(() => toast.error("Erreur chargement anomalies"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const toggle = (key) => {
    setSelected(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const submit = () => {
    if (!selected.length) {
      toast.error("Sélectionnez au moins une anomalie");
      return;
    }
    onSubmit({ anomalies: selected, note });
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[130] p-4" data-testid="ide-anomaly-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden" data-testid="ide-anomaly-modal">
        <div className="p-5 border-b border-slate-200 flex items-start justify-between bg-orange-50">
          <div className="min-w-0">
            <h3 className="font-bold text-slate-900 flex items-center gap-2" data-testid="ide-anomaly-modal-title">
              <AlertOctagon className="w-5 h-5 text-orange-600" />
              Préciser les anomalies
            </h3>
            <p className="text-xs text-slate-600 mt-1" data-testid="ide-anomaly-modal-subtitle">
              {bilan?.patientName || "Patient"} — le médecin recevra ce tri pour décider Go/No-Go.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white rounded-lg" data-testid="ide-anomaly-close-button" type="button">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto max-h-[58vh] space-y-4">
          {loading ? (
            <div className="py-6 text-center text-slate-400 text-sm" data-testid="ide-anomaly-loading">Chargement…</div>
          ) : (
            categories.map((cat) => (
              <div key={cat.key} className="border border-slate-200 rounded-xl p-3" data-testid={`ide-anomaly-category-${cat.key}`}>
                <p className="text-sm font-semibold text-slate-800 mb-2">{cat.label}</p>
                <div className="flex flex-wrap gap-2">
                  {(cat.anomalies || []).map((a) => {
                    const active = selected.includes(a.key);
                    return (
                      <button
                        key={a.key}
                        type="button"
                        onClick={() => toggle(a.key)}
                        className={`px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${active ? "bg-orange-600 text-white border-orange-600" : "bg-white text-slate-700 border-slate-200 hover:bg-orange-50"}`}
                        data-testid={`ide-anomaly-option-${a.key}`}
                      >
                        {a.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1" htmlFor="ide-anomaly-note">Commentaire IDE optionnel</label>
            <textarea
              id="ide-anomaly-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={300}
              rows={3}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              placeholder="Ex : neutropénie sur NFS envoyée par le patient…"
              data-testid="ide-anomaly-note-input"
            />
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 font-semibold" data-testid="ide-anomaly-cancel-button">Annuler</button>
          <button type="button" onClick={submit} disabled={busy || loading || selected.length === 0} className="px-4 py-2 rounded-lg bg-orange-600 text-white font-semibold disabled:opacity-50" data-testid="ide-anomaly-submit-button">
            Transmettre anomalie(s) au médecin
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function BilanCard({ bilan, mode, busy, onIdeValidate, onIdeAskDoctor, onDoctorValidate, onDoctorRefuse }) {
  const [bio, setBio] = useState(null);
  const [loadingBio, setLoadingBio] = useState(false);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrNote, setOcrNote] = useState("");
  // Enriched fields come directly from list endpoint (no more N+1 on /patients/{id})
  const patientName = bilan.patientName || "Patient";
  const pathology = bilan.pathology || null;
  const lastProtocol = bilan.lastProtocol || null;

  useEffect(() => {
    let cancelled = false;
    setLoadingBio(true);
    api.get(`/pre-chemo-bilans/${bilan.id}`)
      .then(({ data }) => {
        if (cancelled) return;
        setBio(data.biologyResult || null);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingBio(false); });
    return () => { cancelled = true; };
  }, [bilan.id]);

  const isIde = mode === "ide";

  const ocr = bio?.ocrExtraction || (bio?.ocrStatus && bio.ocrStatus !== "not_applicable" ? { status: bio.ocrStatus, values: [], warnings: [] } : null);
  const ocrValues = Array.isArray(ocr?.values) ? ocr.values : [];
  const ocrSuggestions = Array.isArray(ocr?.suggestedAnomalyDetails) ? ocr.suggestedAnomalyDetails : [];
  const ocrSuggestedKeys = Array.isArray(ocr?.suggestedAnomalies) ? ocr.suggestedAnomalies : [];
  const ocrCompleted = ocr?.status === "completed";
  const ocrValidated = Boolean(ocr?.validated || ocr?.validation);

  const validateOcr = async (accepted) => {
    setOcrBusy(true);
    try {
      const { data } = await api.post(`/pre-chemo-bilans/${bilan.id}/ocr-validation`, {
        accepted,
        selectedAnomalies: accepted ? ocrSuggestedKeys : [],
        note: ocrNote,
      });
      toast.success(data?.message || "Lecture OCR enregistrée");
      setBio(prev => prev ? {
        ...prev,
        ocrExtraction: {
          ...prev.ocrExtraction,
          validated: true,
          validation: {
            accepted,
            selectedAnomalies: accepted ? ocrSuggestedKeys : [],
            note: ocrNote || null,
          },
        },
      } : prev);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'enregistrer la validation OCR");
    } finally {
      setOcrBusy(false);
    }
  };

  const openPdf = async () => {
    if (!bio || loadingBio) return;
    const t = toast.loading("Chargement du PDF…");
    try {
      const resp = await api.get(`/lab/results/${bio.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const fileName = `bilan-${patientName || "patient"}-${bio.sampleDate || bio.id}.pdf`.replace(/\s+/g, "-");
      // Use an anchor click — preserves the user gesture so the browser doesn't block
      // the new tab, and provides a download fallback on mobile where inline PDFs fail.
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.dismiss(t);
    } catch {
      toast.dismiss(t);
      toast.error("Impossible d'ouvrir le PDF");
    }
  };

  return (
    <div className="border border-slate-200 rounded-xl p-3 sm:p-4 bg-white hover:shadow-sm transition-shadow" data-testid={`bilan-card-${bilan.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3 mb-3">
        <div className="min-w-0">
          <p className="font-semibold text-slate-900 truncate">
            {patientName || "Patient"}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            Prélèvement : {bio?.sampleDate || bilan.bilanDate || "—"}
          </p>
        </div>
        <SourceBadge source={bio?.source || bilan.source} labId={bio?.labId} uploadedByRole={bio?.uploadedByRole || bilan.uploadedByRole} />
      </div>

      {(pathology || lastProtocol) && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`bilan-context-${bilan.id}`}>
          {pathology && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-rose-50 border border-rose-100 text-rose-700 text-[11px] font-medium max-w-full"
              data-testid={`bilan-pathology-${bilan.id}`}
              title={pathology}
            >
              <span className="w-1 h-1 rounded-full bg-rose-500" />
              <span className="truncate">{pathology}</span>
            </span>
          )}
          {lastProtocol && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-50 border border-indigo-100 text-indigo-700 text-[11px] font-medium max-w-full"
              data-testid={`bilan-protocol-${bilan.id}`}
              title={lastProtocol}
            >
              <FlaskConical className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{lastProtocol}</span>
            </span>
          )}
        </div>
      )}

      {(bio?.hasAnomalies || (bilan.ideAction?.anomalies || []).length > 0 || (bilan.intakeQualification?.anomalies || []).length > 0) && (
        <div className="mb-3 p-2 rounded-lg bg-orange-50 border border-orange-200" data-testid={`bilan-anomalies-${bilan.id}`}>
          <div className="flex items-center gap-2 mb-1.5 text-orange-800 text-xs font-medium">
            <AlertOctagon className="w-4 h-4 flex-shrink-0" />
            {((bio?.anomalyDetails || bilan.ideAction?.anomalyDetails || bilan.intakeQualification?.anomalyDetails || []).length || (bio?.anomalies || bilan.ideAction?.anomalies || bilan.intakeQualification?.anomalies || []).length || 0)} anomalie(s) signalée(s)
          </div>
          <div className="flex flex-wrap gap-1">
            {(bio?.anomalyDetails || bilan.ideAction?.anomalyDetails || bilan.intakeQualification?.anomalyDetails || []).map((a) => {
              const severityClass =
                a.severity === "critical"
                  ? "bg-red-100 text-red-800 border-red-300"
                  : a.severity === "moderate"
                  ? "bg-orange-100 text-orange-800 border-orange-300"
                  : "bg-amber-100 text-amber-800 border-amber-300";
              return (
                <span
                  key={a.key}
                  className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[11px] font-medium ${severityClass}`}
                  data-testid={`bilan-anomaly-${a.key}`}
                  title={`${a.label} (${a.severity})`}
                >
                  {a.label}
                </span>
              );
            })}
            {/* Fallback : si pas de détails (ex: anciens bilans), afficher les keys brutes */}
            {!(bio?.anomalyDetails || bilan.ideAction?.anomalyDetails || bilan.intakeQualification?.anomalyDetails) && (bio?.anomalies || bilan.ideAction?.anomalies || bilan.intakeQualification?.anomalies || []).map((k) => (
              <span key={k} className="inline-flex items-center px-1.5 py-0.5 rounded border bg-orange-100 text-orange-800 border-orange-300 text-[11px] font-medium">
                {k}
              </span>
            ))}
          </div>
          {(bio?.note || bilan.ideAction?.note || bilan.intakeQualification?.note) && (
            <p className="mt-1.5 text-[11px] text-orange-900/80 italic">
              <span className="font-medium not-italic">Note :</span> {bio?.note || bilan.ideAction?.note || bilan.intakeQualification?.note}
            </p>
          )}
        </div>
      )}

      {ocr && (
        <div className="mb-3 rounded-xl border border-sky-200 bg-sky-50/70 p-3" data-testid={`bilan-ocr-panel-${bilan.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-bold text-sky-900" data-testid={`bilan-ocr-title-${bilan.id}`}>
                Lecture OCR proposée par Hana
              </p>
              <p className="mt-0.5 text-[11px] leading-4 text-sky-800" data-testid={`bilan-ocr-warning-${bilan.id}`}>
                Aide à la lecture uniquement — validation IDE/médecin obligatoire avant usage clinique.
              </p>
            </div>
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${ocrCompleted ? "bg-white text-sky-700 border border-sky-200" : "bg-slate-100 text-slate-500 border border-slate-200"}`} data-testid={`bilan-ocr-status-${bilan.id}`}>
              {ocr.status === "queued" ? "Analyse en cours" : ocrCompleted ? `Confiance ${ocr.confidence || 0}%` : "Non disponible"}
            </span>
          </div>

          {ocrValues.length > 0 && (
            <div className="mt-2 grid grid-cols-2 gap-1.5" data-testid={`bilan-ocr-values-${bilan.id}`}>
              {ocrValues.slice(0, 8).map((v) => {
                const abnormal = v.flag === "low" || v.flag === "high";
                return (
                  <div key={`${v.key}-${v.label}`} className={`rounded-lg border px-2 py-1.5 text-[11px] ${abnormal ? "border-orange-200 bg-orange-50 text-orange-900" : "border-sky-100 bg-white text-slate-700"}`} data-testid={`bilan-ocr-value-${v.key}`}>
                    <span className="block font-semibold truncate">{v.label}</span>
                    <span className="block truncate">{v.rawValue || v.value || "—"} {v.unit || ""}</span>
                  </div>
                );
              })}
            </div>
          )}

          {ocrSuggestions.length > 0 && (
            <div className="mt-2" data-testid={`bilan-ocr-suggestions-${bilan.id}`}>
              <p className="text-[11px] font-semibold text-orange-900 mb-1">Anomalies suggérées à vérifier :</p>
              <div className="flex flex-wrap gap-1">
                {ocrSuggestions.map((a) => (
                  <span key={a.key} className="rounded border border-orange-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-orange-800" data-testid={`bilan-ocr-suggestion-${a.key}`}>
                    {a.label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {(ocr.warnings || []).length > 0 && (
            <p className="mt-2 text-[11px] leading-4 text-slate-600" data-testid={`bilan-ocr-warnings-${bilan.id}`}>
              {(ocr.warnings || []).slice(0, 2).join(" · ")}
            </p>
          )}

          {isIde && ocrCompleted && !ocrValidated && (
            <div className="mt-3 space-y-2" data-testid={`bilan-ocr-validation-block-${bilan.id}`}>
              <input
                value={ocrNote}
                onChange={(e) => setOcrNote(e.target.value)}
                maxLength={300}
                placeholder="Note IDE sur la lecture OCR (optionnel)"
                className="w-full rounded-lg border border-sky-200 bg-white px-2 py-1.5 text-xs text-slate-700"
                data-testid={`bilan-ocr-note-input-${bilan.id}`}
              />
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => validateOcr(true)}
                  disabled={ocrBusy}
                  className="rounded-lg bg-sky-700 px-2 py-2 text-xs font-semibold text-white disabled:opacity-50"
                  data-testid={`bilan-ocr-accept-button-${bilan.id}`}
                >
                  Valider la lecture OCR
                </button>
                <button
                  type="button"
                  onClick={() => validateOcr(false)}
                  disabled={ocrBusy}
                  className="rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs font-semibold text-slate-700 disabled:opacity-50"
                  data-testid={`bilan-ocr-reject-button-${bilan.id}`}
                >
                  Marquer non fiable
                </button>
              </div>
            </div>
          )}

          {ocrValidated && (
            <p className="mt-2 text-[11px] font-semibold text-emerald-700" data-testid={`bilan-ocr-validated-${bilan.id}`}>
              Lecture OCR revue par l’équipe — ne remplace pas le tri RAS/anomalie.
            </p>
          )}
        </div>
      )}

      <button
        onClick={openPdf}
        disabled={!bio || loadingBio}
        className="w-full mb-3 px-3 py-2 text-xs font-medium text-slate-700 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid={`bilan-open-pdf-${bilan.id}`}
      >
        <FileText className="w-4 h-4" />
        Ouvrir le PDF
      </button>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {isIde ? (
          <>
            <button
              onClick={onIdeValidate}
              disabled={busy}
              className="px-3 py-3 sm:py-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 rounded-lg flex items-center justify-center gap-1 min-h-[44px]"
              data-testid={`bilan-ide-ras-${bilan.id}`}
            >
              <CheckCircle2 className="w-4 h-4" />
              RAS → médecin
            </button>
            <button
              onClick={onIdeAskDoctor}
              disabled={busy}
              className="px-3 py-3 sm:py-2 text-sm font-semibold text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 disabled:opacity-50 rounded-lg flex items-center justify-center gap-1 min-h-[44px]"
              data-testid={`bilan-ide-anomaly-${bilan.id}`}
            >
              <AlertOctagon className="w-4 h-4" />
              Anomalie → médecin
            </button>
          </>
        ) : (
          <>
            <button
              onClick={onDoctorValidate}
              disabled={busy}
              className="px-3 py-3 sm:py-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 rounded-lg flex items-center justify-center gap-1 min-h-[44px]"
              data-testid={`bilan-doc-validate-${bilan.id}`}
            >
              <CheckCircle2 className="w-4 h-4" />
              Valider
            </button>
            <button
              onClick={onDoctorRefuse}
              disabled={busy}
              className="px-3 py-3 sm:py-2 text-sm font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 disabled:opacity-50 rounded-lg flex items-center justify-center gap-1 min-h-[44px]"
              data-testid={`bilan-doc-refuse-${bilan.id}`}
            >
              <AlertOctagon className="w-4 h-4" />
              Refuser
            </button>
          </>
        )}
      </div>
    </div>
  );
}
